import { MAX_OPEN_TABS } from '../../constants';
import type { OpenTab, SliceCreator, TabsActions } from './types';

const dropOldestUntilUnderLimit = (tabs: OpenTab[]): OpenTab[] =>
  tabs.length <= MAX_OPEN_TABS ? tabs : tabs.slice(tabs.length - MAX_OPEN_TABS);

const upsertTab = (tabs: OpenTab[], next: OpenTab): OpenTab[] => {
  const idx = tabs.findIndex((t) => t.sessionId === next.sessionId);
  if (idx === -1) return dropOldestUntilUnderLimit([...tabs, next]);
  const updated = [...tabs];
  updated[idx] = next;
  return updated;
};

export const createTabsSlice: SliceCreator<TabsActions> = (set) => ({
  openTab: (tab) =>
    set((s) => ({
      openTabs: upsertTab(s.openTabs, tab),
      activeSessionId: tab.sessionId,
      mode: s.mode === 'minimized' ? 'floating' : s.mode,
      draftOpen: false,
    })),

  closeTab: (sessionId) =>
    set((s) => {
      const remaining = s.openTabs.filter((t) => t.sessionId !== sessionId);
      const wasActive = s.activeSessionId === sessionId;
      const { [sessionId]: _, ...remainingMessages } = s.messagesBySession;
      // If the closed tab is the one we were streaming, drop the draft.
      const isStreamingThisTab = s.streamingDraft?.sessionId === sessionId;
      return {
        openTabs: remaining,
        activeSessionId: wasActive
          ? (remaining[remaining.length - 1]?.sessionId ?? null)
          : s.activeSessionId,
        messagesBySession: remainingMessages,
        streamingDraft: isStreamingThisTab ? null : s.streamingDraft,
      };
    }),

  setActive: (sessionId) => set({ activeSessionId: sessionId }),

  setTabStatus: (sessionId, status) =>
    set((s) => ({
      openTabs: s.openTabs.map((t) =>
        t.sessionId === sessionId ? { ...t, status } : t
      ),
    })),

  setTabTitle: (sessionId, title) =>
    set((s) => ({
      openTabs: s.openTabs.map((t) =>
        t.sessionId === sessionId ? { ...t, title } : t
      ),
    })),

  migrateSession: (fromId, toId) =>
    set((s) => {
      if (fromId === toId) return s;
      const messages = { ...s.messagesBySession };
      if (messages[fromId]) {
        messages[toId] = messages[fromId];
        delete messages[fromId];
      }
      const seen = new Set<string>();
      const tabs = s.openTabs
        .map((t) => (t.sessionId === fromId ? { ...t, sessionId: toId } : t))
        .filter((t) => {
          if (seen.has(t.sessionId)) return false;
          seen.add(t.sessionId);
          return true;
        });
      const drafts = { ...s.draftBySession };
      if (drafts[fromId]) {
        drafts[toId] = drafts[fromId];
        delete drafts[fromId];
      }
      const queued = { ...s.queuedBySession };
      if (queued[fromId]) {
        queued[toId] = queued[fromId];
        delete queued[fromId];
      }
      return {
        messagesBySession: messages,
        openTabs: tabs,
        draftBySession: drafts,
        queuedBySession: queued,
        activeSessionId:
          s.activeSessionId === fromId ? toId : s.activeSessionId,
      };
    }),
});
